import { useCallback, useMemo, useState } from 'react'
import { isValid } from 'date-fns'

import { Badge } from '@wartek-id/badge'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import { CardKSSchool } from 'app/KinerjaShared/components/CardKSSchool'
import { PltKepalaSekolahConfirmationDialog } from 'app/KinerjaShared/components/PltKepalaSekolahConfirmationDialog'
import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'
import { assetUrl } from 'utils/url'
import { usePelaksanaanDate } from 'app/KinerjaShared/hooks/usePelaksanaanDate'
import { formatDate } from 'utils/datetime'
import CardPeriodType from 'app/KinerjaShared/components/CardPeriodType'

import type { PelaksanaanSchool } from 'types/kinerja-pelaksanaan'

type PenilaianKinerjaPegawaiCardProps = {
  schools: PelaksanaanSchool[]
  badgeText?: string
  kepalaSekolahLabel?: string
  pegawaiLabel?: string
  resolveNextPath: (school: PelaksanaanSchool) => string
}

export function PenilaianKinerjaPegawaiCard({
  schools,
  pegawaiLabel = 'Guru',
  badgeText = 'Khusus Kepala Sekolah',
  kepalaSekolahLabel = 'Kepala Sekolah',
  resolveNextPath,
}: PenilaianKinerjaPegawaiCardProps) {
  // -----------------------------------------
  // Resolve pelaksanaan date
  // -----------------------------------------
  const { isStarted: isPelaksanaanStarted, date: pelaksanaanDate } =
    usePelaksanaanDate()

  // -----------------------------------------
  // Resolve schools ready for penilaian
  // -----------------------------------------
  const schoolsReadyForPenilaian = useMemo(
    () => schools.filter((school) => school.perencanaanReadyState !== 'EMPTY'),
    [schools]
  )

  // -----------------------------------------
  // Handle dialog confirmation
  // -----------------------------------------
  const [showDialogPltConfirmation, setShowDialogPltConfirmation] =
    useState(false)

  const handleShowDialogPltConfirmation = () => {
    setShowDialogPltConfirmation(true)
  }

  const renderSchools = useCallback(() => {
    return schoolsReadyForPenilaian.map((school) => {
      const nextPath = resolveNextPath(school)

      return (
        <CardKSSchool
          key={school.npsn}
          nextText="Lakukan"
          nextPath={nextPath}
          onConfirmClick={
            school.statusSatuanPendidikan === 'NEGERI'
              ? handleShowDialogPltConfirmation
              : undefined
          }
          confirmationStatus={school.confirmationStatus}
          schoolName={school.schoolName}
          schoolStatus={school.statusSatuanPendidikan}
          schoolRole={school.role}
          npsn={school.npsn}
        />
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolsReadyForPenilaian])

  // Render nothing if there is no school ready for penilaian
  if (schoolsReadyForPenilaian.length === 0) {
    return null
  }

  // Render the not started banner if the pelaksanaan period is not started yet
  if (schoolsReadyForPenilaian.length > 0 && !isPelaksanaanStarted) {
    return (
      <PelaksanaanNotStartedBanner
        pelaksanaanDate={pelaksanaanDate}
        pegawaiLabel={pegawaiLabel}
        badgeText={badgeText}
      />
    )
  }

  return (
    <section className="shadow-card rounded-lg p-6 bg-white">
      <div className="flex items-center gap-6">
        <img
          alt=""
          src={assetUrl('/illustrations/img-book-rapor-graphic.png')}
          width={84}
          height={84}
          className="flex-shrink-0"
        />
        <div>
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <Text variant="heading-lg" as="h2">
              Penilaian Kinerja dan Penetapan Predikat Kinerja
            </Text>
            {badgeText && (
              <Badge className="uppercase whitespace-nowrap">{badgeText}</Badge>
            )}
          </div>
          <Text color="subdued">
            Lakukan untuk seluruh {pegawaiLabel} di satuan pendidikan Anda.
          </Text>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4 mt-6">
        {renderSchools()}
      </div>

      <PltKepalaSekolahConfirmationDialog
        isOpen={showDialogPltConfirmation}
        schools={schools}
        kepalaSekolahLabel={kepalaSekolahLabel}
        onClose={() => setShowDialogPltConfirmation(false)}
      />

      <div className="rounded bg-cyan-5 p-3 mt-6 flex items-center">
        <Icon color="informational" className="mr-1">
          error
        </Icon>
        Jika satuan pendidikan tidak sesuai, mohon kunjungi&nbsp;
        <a
          className="!text-blue-70 underline"
          href={PANDUAN_PENGELOLAAN_URL}
          target="_blank"
          rel="noreferrer"
        >
          Pusat Bantuan
        </a>
        .
      </div>
    </section>
  )
}

type PelaksanaanNotStartedBannerProps = {
  pelaksanaanDate: string
  badgeText?: string
  pegawaiLabel?: string
}
export function PelaksanaanNotStartedBanner({
  pelaksanaanDate,
  badgeText = 'Khusus Kepala Sekolah',
  pegawaiLabel = 'Guru',
}: PelaksanaanNotStartedBannerProps) {
  if (!isValid(new Date(pelaksanaanDate))) return null

  const pelaksanaanMonth: string = formatDate(pelaksanaanDate, 'MMMM yyyy')

  return (
    <section className="shadow-card rounded-lg bg-white flex items-center gap-6 p-6">
      <div className="flex flex-col">
        <img
          alt=""
          src={assetUrl('/illustrations/img-time-document.png')}
          width={84}
          height={84}
        />
      </div>
      <div className="flex-1 flex flex-col gap-1 justify-center">
        <Text
          variant="subheading"
          color="informational"
          className="!text-blue-60 !uppercase tracking-[0.5px]"
        >
          <CardPeriodType withYear />
        </Text>

        <div className="flex items-center gap-2">
          <Text variant="heading-lg" as="h2">
            Menunggu periode Pelaksanaan Kinerja
          </Text>
          <Badge className="uppercase whitespace-nowrap">{badgeText}</Badge>
        </div>
        <Text color="subdued">
          Perencanaan {pegawaiLabel} sudah selesai dan disetujui. Anda dapat
          lanjut ke Pelaksanaan mulai dari <strong>{pelaksanaanMonth}</strong>.
          Cek lagi halaman ini nanti, ya.
        </Text>
      </div>
    </section>
  )
}
